import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DataTable, {
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeader,
  DataTableRow,
} from '@amzn/meridian/data-table';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import Image from '@amzn/meridian/image';
import Button from '@amzn/meridian/button';
import MeridianLoader from '@amzn/meridian/loader';
import { PageLoader } from 'src/components/common/loaders/page-loader';
import { TablePagination } from 'src/components/common/data-table-action-bar/TablePagination';
import { CursorPagination } from 'src/components/titles/CursorPagination';
import { sanitizeMeridianImageSrc } from 'src/utils';
import { formatUtcDate } from 'src/utils/formatDateTime';
import { useTranslation } from 'src/arb-wrapper';
import { FEATURE_FLAGS } from 'src/constants';
import { isFeatureEnabled } from 'src/feature-flags';

export interface TitleInfo {
  gti: string;
  name: string;
  description?: string;
  genres?: string[];
  coverImageUrl?: string;
  campaignNames?: string[];
  campaignIds?: string[];
  /** Resolved product-strategy display names this title belongs to (campaign detail context). */
  strategyNames?: string[];
  releaseDate?: string;
  contributors?: {
    actors: string[];
    producers: string[];
    directors: string[];
  };
}

export interface ExclusionListProps {
  excludedGtis: Set<string>;
  isLoading: boolean;
  onExclude: (gti: string) => Promise<void>;
  onInclude: (gti: string) => Promise<void>;
  mutatingGtis: Set<string>;
}

export interface TitlesTableProps {
  titles: TitleInfo[];
  isLoading: boolean;
  hasError: boolean;
  /** When true, enrichment data (cover image, description, release date, genres) is still loading — shows skeleton placeholders for those columns */
  isEnriching?: boolean;
  /** When true, shows a linear loading indicator next to the showing count */
  isFetching?: boolean;
  /** When provided, asset count links include this campaignId as a filter */
  campaignId?: string;
  /** When true, hides the Campaigns column (e.g. in campaign detail context) */
  hideCampaigns?: boolean;
  /** When true, shows a Product Strategy column (campaign detail context) */
  showStrategies?: boolean;
  /** Server-side pagination — when provided, disables client-side pagination */
  serverPagination?: {
    page: number;
    pageSize: number;
    total: number;
    pageCount: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
  };
  /** Cursor-based pagination — uses Next/Previous buttons instead of page numbers */
  cursorPagination?: {
    page: number;
    pageSize: number;
    hasMore: boolean;
    isLoading?: boolean;
    onNextPage: () => void;
    onPreviousPage: () => void;
    onPageSizeChange: (pageSize: number) => void;
  };
  /** When provided, renders an Action column with Exclude/Include buttons */
  exclusionList?: ExclusionListProps;
}

const CAMPAIGN_DISPLAY_LIMIT = 3;

const CampaignNamesList: React.FC<{ names: string[]; ids?: string[] }> = ({ names, ids }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  if (!names.length) return <Text type="b300">-</Text>;

  const hasMore = names.length > CAMPAIGN_DISPLAY_LIMIT;
  const visible = expanded ? names : names.slice(0, CAMPAIGN_DISPLAY_LIMIT);
  const visibleIds = ids ? (expanded ? ids : ids.slice(0, CAMPAIGN_DISPLAY_LIMIT)) : undefined;

  return (
    <Column spacing="100">
      {visible.map((name, i) => {
        const campaignId = visibleIds?.[i];
        return campaignId
          ? (
            <Link
              key={i}
              style={{ color: '#0073bb' }}
              to={`/campaigns/${campaignId}`}
            >
              {name}
            </Link>
            )
          : <Text key={i} type="b300">{name}</Text>;
      })}
      {hasMore && (
        <Button
          size="small"
          type="link"
          onClick={() => { setExpanded((prev) => !prev); }}
        >
          {expanded
            ? t('show_less')
            : t('show_more_campaigns', { count: String(names.length - CAMPAIGN_DISPLAY_LIMIT) })}
        </Button>
      )}
    </Column>
  );
};

const EnrichmentSkeleton: React.FC = () => (
  <div
    data-testid="enrichment-skeleton"
    style={{
      width: '100%',
      height: '1em',
      borderRadius: 4,
      backgroundColor: '#E9EBEF',
      animation: 'pulse 1.5s ease-in-out infinite',
    }}
  />
);

export const TitlesTable: React.FC<TitlesTableProps> = ({
  titles,
  isLoading,
  hasError,
  isEnriching,
  isFetching,
  campaignId,
  hideCampaigns,
  showStrategies,
  serverPagination,
  cursorPagination,
  exclusionList,
}) => {
  const { t } = useTranslation();
  const isReleaseDateEnabled = isFeatureEnabled(
    FEATURE_FLAGS.ENABLE_ASSET_IMPROVEMENTS,
  );

  // Client-side pagination fallback when no server/cursor pagination
  const [localPage, setLocalPage] = useState(1);
  const [localPageSize, setLocalPageSize] = useState(10);

  // Reset to page 1 when titles data changes (e.g. re-fetch with fewer items)
  useEffect(() => { setLocalPage(1); }, [titles]);

  const useServerPagination = !!serverPagination;
  const useCursorPagination = !!cursorPagination;
  const displayTitles = (useServerPagination || useCursorPagination)
    ? titles
    : titles.slice((localPage - 1) * localPageSize, localPage * localPageSize);

  const pageCount = useServerPagination
    ? serverPagination!.pageCount
    : Math.ceil(titles.length / localPageSize);
  const currentPage = useServerPagination ? serverPagination!.page : localPage;
  const pageSize = useServerPagination ? serverPagination!.pageSize : localPageSize;

  const baseColumnCount = isReleaseDateEnabled ? 7 : 6;
  const columnCount = baseColumnCount + (hideCampaigns ? 0 : 1) + (showStrategies ? 1 : 0) + (exclusionList ? 1 : 0);

  return (
    <div style={{ width: '100%' }}>
      <Column spacing="400">

        {isFetching && !isLoading && (
          <div style={{ width: 100 }}>
            <MeridianLoader size="small" type="linear" />
          </div>
        )}

        {isLoading
          ? (
          <Column alignmentHorizontal="center" alignmentVertical="center" height="300px">
            <PageLoader message={t('titles_loading')} />
          </Column>
            )
          : (
          <DataTable
            data-testid="titles-table"
            showBorder={true}
            showRowDividers={true}
            size="medium"
            stickyHeaderRows={true}
            width="100%"
          >
            <DataTableHead backgroundColor="secondary">
              <DataTableRow>
                <DataTableHeader width={72}></DataTableHeader>
                <DataTableHeader width={200}>{t('campaign_titles_table_header_title')}</DataTableHeader>
                <DataTableHeader width={280}>{t('campaign_titles_table_header_description')}</DataTableHeader>
                {isReleaseDateEnabled && (
                  <DataTableHeader width={120}>
                    {t('campaign_titles_table_header_release_date')}
                  </DataTableHeader>
                )}
                {!hideCampaigns && <DataTableHeader width={180}>{t('campaign_titles_table_header_campaigns')}</DataTableHeader>}
                {showStrategies && <DataTableHeader width={160}>{t('campaign_titles_table_header_strategy')}</DataTableHeader>}
                <DataTableHeader width={140}>{t('campaign_titles_table_header_genres')}</DataTableHeader>
                <DataTableHeader width={80}>{t('campaign_titles_table_header_assets')}</DataTableHeader>
                <DataTableHeader width={180}>{t('campaign_titles_table_header_gti')}</DataTableHeader>
                {exclusionList && <DataTableHeader width={100}>{t('campaign_titles_table_header_action')}</DataTableHeader>}
              </DataTableRow>
            </DataTableHead>
            <DataTableBody>
              {hasError && (
                <DataTableRow>
                  <DataTableCell colSpan={columnCount}>{t('titles_unable_to_load')}</DataTableCell>
                </DataTableRow>
              )}
              {!hasError && displayTitles.length === 0 && (
                <DataTableRow>
                  <DataTableCell colSpan={columnCount}>{t('table_no_results')}</DataTableCell>
                </DataTableRow>
              )}
                {!hasError && displayTitles.map((titleInfo, index) => (
                  <DataTableRow key={titleInfo.gti}>
                    <DataTableCell>
                      {isEnriching
                        ? (
                          <div data-testid={`title-cover-skeleton-${index}`} style={{ width: '64px', height: '64px', borderRadius: 4, backgroundColor: '#E9EBEF' }} />
                          )
                        : titleInfo.coverImageUrl && (
                        <div style={{ width: '64px', height: '64px' }}>
                          <Image
                            data-testid={`title-cover-${index}`}
                            size="contain"
                            src={sanitizeMeridianImageSrc(titleInfo.coverImageUrl)}
                            viewportAspectRatio={1}
                          />
                        </div>
                        )}
                    </DataTableCell>
                    <DataTableCell>{titleInfo.name || '-'}</DataTableCell>
                    <DataTableCell>
                      {isEnriching
                        ? <EnrichmentSkeleton />
                        : (
                        <div style={{ maxWidth: 280 }}>
                          <Text breakWord={true}>
                            {titleInfo.description
                              ? titleInfo.description.length > 160
                                ? `${titleInfo.description.substring(0, 160)}...`
                                : titleInfo.description
                              : '-'}
                          </Text>
                        </div>
                          )}
                    </DataTableCell>
                    {isReleaseDateEnabled && (
                      <DataTableCell data-testid={`title-release-date-${index}`}>
                        {isEnriching
                          ? <EnrichmentSkeleton />
                          : formatUtcDate(titleInfo.releaseDate, '-')}
                      </DataTableCell>
                    )}
                    {!hideCampaigns && (
                      <DataTableCell>
                        <CampaignNamesList ids={titleInfo.campaignIds} names={titleInfo.campaignNames ?? []} />
                      </DataTableCell>
                    )}
                    {showStrategies && (
                      <DataTableCell>
                        <div style={{ maxWidth: 160, wordBreak: 'break-word' }}>
                          <Text breakWord={true} data-testid={`title-strategies-${index}`}>
                            {titleInfo.strategyNames?.length ? titleInfo.strategyNames.join(', ') : '-'}
                          </Text>
                        </div>
                      </DataTableCell>
                    )}
                    <DataTableCell>
                      {isEnriching ? <EnrichmentSkeleton /> : (titleInfo.genres?.length ? titleInfo.genres.join(', ') : '-')}
                    </DataTableCell>
                    <DataTableCell>
                      <Link
                        data-testid={`asset-count-link-${index}`}
                        style={{ color: '#0073bb', textDecoration: 'none' }}
                        to={`/assets?gti=${encodeURIComponent(titleInfo.gti)}${campaignId ? `&campaignId=${encodeURIComponent(campaignId)}` : ''}`}
                      >
                        {t('campaign_view_assets')}
                      </Link>
                    </DataTableCell>
                    <DataTableCell>{titleInfo.gti}</DataTableCell>
                    {exclusionList && (
                      <DataTableCell>
                        {(() => {
                          const isExcluded = exclusionList.excludedGtis.has(titleInfo.gti);
                          const isMutating = exclusionList.mutatingGtis.has(titleInfo.gti);
                          const isDisabled = exclusionList.isLoading || isMutating;
                          return isExcluded
                            ? (
                            <Button
                              data-testid={`include-btn-${index}`}
                              disabled={isDisabled}
                              size="small"
                              type="primary"
                              onClick={() => { exclusionList.onInclude(titleInfo.gti); }}
                            >
                              {isMutating ? <MeridianLoader size="small" /> : t('campaign_titles_include_button')}
                            </Button>
                              )
                            : (
                            <Button
                              data-testid={`exclude-btn-${index}`}
                              disabled={isDisabled}
                              size="small"
                              type="secondary"
                              onClick={() => { exclusionList.onExclude(titleInfo.gti); }}
                            >
                              {isMutating ? <MeridianLoader size="small" /> : t('campaign_titles_exclude_button')}
                            </Button>
                              );
                        })()}
                      </DataTableCell>
                    )}
                  </DataTableRow>
                ))}
                </DataTableBody>
              </DataTable>
            )}

        {cursorPagination
          ? (
          <CursorPagination
            hasMore={cursorPagination.hasMore}
            isLoading={cursorPagination.isLoading}
            page={cursorPagination.page}
            pageSize={cursorPagination.pageSize}
            onNextPage={cursorPagination.onNextPage}
            onPageSizeChange={cursorPagination.onPageSizeChange}
            onPreviousPage={cursorPagination.onPreviousPage}
          />
            )
          : (
          <TablePagination
            currentPage={currentPage}
            itemsPerPage={pageSize}
            itemsPerPageOptions={[10, 25, 50, 100]}
            pageCount={pageCount}
            onItemsPerPageChange={(size) => {
              if (useServerPagination) {
                serverPagination!.onPageSizeChange(size);
              } else {
                setLocalPageSize(size);
                setLocalPage(1);
              }
            }}
            onPageChange={(page) => {
              if (useServerPagination) {
                serverPagination!.onPageChange(page);
              } else {
                setLocalPage(page);
              }
            }}
          />
            )}
      </Column>
    </div>
  );
};
